import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { translationsApi, translationCenterApi } from '../lib/api';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
    Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import DynamicContentTab from '../components/admin/DynamicContentTab';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
    Languages, 
    Download, 
    Upload, 
    Plus, 
    Search,
    AlertCircle,
    Check,
    Trash2,
    Loader2,
    FileText,
    Lock,
    Filter,
    Sparkles
} from 'lucide-react';

// Language display names
const LANGUAGE_NAMES = {
    en: 'English',
    da: 'Danish',
    de: 'German',
    fr: 'French',
    es: 'Spanish',
    nl: 'Dutch',
    sv: 'Swedish',
    no: 'Norwegian',
};

export default function TranslationsPage() {
    const { user, isAdmin } = useAuth();
    const [languages, setLanguages] = useState(['en']);
    const [selectedLang, setSelectedLang] = useState('da');
    const [keys, setKeys] = useState([]);
    const [stats, setStats] = useState({ total_keys: 0, translated: 0, missing: 0 });
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showOnlyMissing, setShowOnlyMissing] = useState(false);
    const [showAutoCaptured, setShowAutoCaptured] = useState(false);
    const [editingCell, setEditingCell] = useState(null);
    const [editingEnCell, setEditingEnCell] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [editEnValue, setEditEnValue] = useState('');
    const [saving, setSaving] = useState(false);
    const [savingEn, setSavingEn] = useState(false);
    // Mojibake scan state — populated on-demand via the "Check encoding
    // issues" button. Shape: { total_affected, translation_keys: {...},
    // translation_values: {...} } or null when no scan has run yet.
    const [mojibakeScan, setMojibakeScan] = useState(null);
    const [scanningMojibake, setScanningMojibake] = useState(false);
    const [showMojibakeDialog, setShowMojibakeDialog] = useState(false);
    
    // Dialogs
    const [showAddKeyDialog, setShowAddKeyDialog] = useState(false);
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [keyToDelete, setKeyToDelete] = useState(null);
    const [newKey, setNewKey] = useState({ key: '', en: '', description: '' });
    const [importResult, setImportResult] = useState(null);
    
    const fileInputRef = useRef(null);

    // Fetch languages on mount
    useEffect(() => {
        const fetchLanguages = async () => {
            try {
                const response = await translationsApi.getLanguages();
                setLanguages(response.data.languages.filter(l => l !== 'en'));
            } catch (error) {
                toast.error('Failed to load languages');
            }
        };
        fetchLanguages();
    }, []);

    // Fetch keys and stats when language changes
    useEffect(() => {
        fetchData();
    }, [selectedLang]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [keysRes, statsRes] = await Promise.all([
                translationsApi.getKeys(selectedLang),
                translationsApi.getStats(selectedLang)
            ]);
            setKeys(keysRes.data.keys || []);
            setStats(statsRes.data);
        } catch (error) {
            toast.error('Failed to load translations');
        } finally {
            setLoading(false);
        }
    };

    // Filter keys by search and missing toggle
    const filteredKeys = keys.filter(k => {
        // Apply search filter
        const matchesSearch = 
            k.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
            k.en.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (k.translation || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (k.description || '').toLowerCase().includes(searchQuery.toLowerCase());
        
        // Apply missing filter
        const matchesMissing = !showOnlyMissing || k.is_missing;
        
        // Apply auto-captured filter
        const matchesAutoCaptured = !showAutoCaptured || k.auto_captured;
        
        return matchesSearch && matchesMissing && matchesAutoCaptured;
    });
    
    // Count auto-captured keys
    const autoCapturedCount = keys.filter(k => k.auto_captured).length;

    // Handle translation edit
    const handleEditStart = (keyId, currentValue) => {
        setEditingCell(keyId);
        setEditValue(currentValue || '');
    };

    const handleEditSave = async (keyId) => {
        if (saving) return;
        
        setSaving(true);
        try {
            await translationsApi.setTranslation(keyId, selectedLang, editValue);
            
            // Update local state
            setKeys(prev => prev.map(k => 
                k.id === keyId 
                    ? { ...k, translation: editValue, is_missing: !editValue }
                    : k
            ));
            
            // Update stats
            const key = keys.find(k => k.id === keyId);
            if (key?.is_missing && editValue) {
                setStats(prev => ({
                    ...prev,
                    translated: prev.translated + 1,
                    missing: prev.missing - 1
                }));
            } else if (!key?.is_missing && !editValue) {
                setStats(prev => ({
                    ...prev,
                    translated: prev.translated - 1,
                    missing: prev.missing + 1
                }));
            }
            
            setEditingCell(null);
            toast.success('Translation saved');
        } catch (error) {
            if (error.response?.status === 403) {
                toast.error('Cannot modify English - it is the source language');
            } else {
                toast.error('Failed to save translation');
            }
        } finally {
            setSaving(false);
        }
    };

    const handleEditCancel = () => {
        setEditingCell(null);
        setEditValue('');
    };

    // ── English master editing ─────────────────────────────────────
    // Separate state from the per-language edit so an admin can have
    // both open simultaneously if they want. Backend endpoint
    // `PATCH /translations/keys/{id}` with just `{en: "…"}` updates
    // only ``translation_keys.en`` — translation_values rows are
    // untouched, so existing translations never auto-overwrite.
    const handleEditEnStart = (keyId, currentValue) => {
        setEditingEnCell(keyId);
        setEditEnValue(currentValue || '');
    };

    const handleEditEnSave = async (keyId) => {
        if (savingEn) return;
        const trimmed = (editEnValue || '').trim();
        if (!trimmed) {
            toast.error('English source cannot be empty');
            return;
        }
        setSavingEn(true);
        try {
            await translationsApi.updateKey(keyId, { en: trimmed });
            setKeys((prev) => prev.map((k) =>
                k.id === keyId ? { ...k, en: trimmed } : k
            ));
            setEditingEnCell(null);
            toast.success('English source updated · existing translations untouched');
        } catch (error) {
            if (error.response?.status === 403) {
                toast.error('Only admins can edit the English source');
            } else {
                toast.error(error.response?.data?.detail || 'Failed to save English source');
            }
        } finally {
            setSavingEn(false);
        }
    };

    const handleEditEnCancel = () => {
        setEditingEnCell(null);
        setEditEnValue('');
    };

    const handleEnKeyPress = (e, keyId) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleEditEnSave(keyId);
        } else if (e.key === 'Escape') {
            handleEditEnCancel();
        }
    };

    // ── Mojibake scan ──────────────────────────────────────────────
    const handleScanMojibake = async () => {
        if (scanningMojibake) return;
        setScanningMojibake(true);
        try {
            const res = await translationsApi.scanMojibake();
            setMojibakeScan(res.data);
            setShowMojibakeDialog(true);
            if (res.data.total_affected === 0) {
                toast.success('No encoding issues found');
            } else {
                toast.warning(`${res.data.total_affected} string(s) with encoding issues — see details`);
            }
        } catch (error) {
            toast.error('Failed to scan for encoding issues');
        } finally {
            setScanningMojibake(false);
        }
    };

    // Handle key press in edit mode
    const handleKeyPress = (e, keyId) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleEditSave(keyId);
        } else if (e.key === 'Escape') {
            handleEditCancel();
        }
    };

    // Validate key format
    const validateKeyFormat = (key) => {
        const pattern = /^[a-z][a-z0-9]*(\.[a-z][a-z0-9]*)*$/;
        return pattern.test(key);
    };

    // Add new key (admin only)
    const handleAddKey = async () => {
        if (!newKey.key || !newKey.en) {
            toast.error('Key and English text are required');
            return;
        }
        
        if (!validateKeyFormat(newKey.key)) {
            toast.error('Invalid key format. Use lowercase words separated by dots (e.g., order.create.title)');
            return;
        }
        
        if (!newKey.en.trim()) {
            toast.error('English text cannot be empty (it is the source of truth)');
            return;
        }
        
        try {
            await translationsApi.createKey(newKey);
            toast.success('Translation key created');
            setShowAddKeyDialog(false);
            setNewKey({ key: '', en: '', description: '' });
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create key');
        }
    };

    // Confirm delete key
    const confirmDeleteKey = (key) => {
        setKeyToDelete(key);
        setShowDeleteDialog(true);
    };

    // Delete key (admin only)
    const handleDeleteKey = async () => {
        if (!keyToDelete) return;
        
        try {
            const response = await translationsApi.deleteKey(keyToDelete.id);
            toast.success(`Key deleted (${response.data.translations_deleted || 0} translations removed)`);
            setKeys(prev => prev.filter(k => k.id !== keyToDelete.id));
            setStats(prev => ({ ...prev, total_keys: prev.total_keys - 1 }));
            setShowDeleteDialog(false);
            setKeyToDelete(null);
        } catch (error) {
            toast.error('Failed to delete key');
        }
    };

    // Export CSV
    const handleExport = async () => {
        try {
            const token = localStorage.getItem('token');
            // Unified Translation Center CSV covers both static keys AND
            // dynamic content (Visual Builder, Campaigns, Landing Pages,
            // Bundles, Affinity Programmes). `source` column drives
            // routing on import.
            const url = translationCenterApi.exportUrl(selectedLang);
            
            const response = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (!response.ok) {
                if (response.status === 403) {
                    toast.error('Cannot export English as target language');
                    return;
                }
                throw new Error('Export failed');
            }
            
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `translation-center_${selectedLang}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
            
            toast.success('CSV exported');
        } catch (error) {
            toast.error('Failed to export');
        }
    };

    // Import CSV — unified: rows with source="static" update the static
    // translations table, rows with a registered dynamic source update
    // entity_translations. Empty value clears the target row.
    const handleImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        try {
            const response = await translationCenterApi.importCsv(selectedLang, file);
            // Adapt unified summary shape onto the existing dialog format
            const s = response.data || {};
            setImportResult({
                imported: (s.static_imported || 0) + (s.dynamic_imported || 0),
                updated: (s.static_updated || 0) + (s.dynamic_updated || 0),
                skipped: (s.static_skipped || 0) + (s.dynamic_skipped || 0),
                unknown_keys: s.unknown_rows || [],
                skipped_reasons: [],
                errors: s.errors || [],
                _breakdown: s,
            });
            setShowImportDialog(true);
            fetchData();
        } catch (error) {
            if (error.response?.status === 403) {
                toast.error('Cannot import English translations - English is the source language');
            } else {
                toast.error(error.response?.data?.detail || 'Failed to import');
            }
        }
        
        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <Header />
            
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Page Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Inter, sans-serif' }}>
                            <Languages className="h-6 w-6 text-indigo-600" />
                            Translation Center
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">
                            One place for all customer-facing translations — static UI strings AND dynamic admin-authored content
                        </p>
                    </div>
                    
                    <div className="flex items-center gap-3">
                        {/* Language Selector */}
                        <Select value={selectedLang} onValueChange={setSelectedLang}>
                            <SelectTrigger className="w-[180px]" data-testid="language-selector">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {languages.map(lang => (
                                    <SelectItem key={lang} value={lang}>
                                        {LANGUAGE_NAMES[lang] || lang.toUpperCase()}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        
                        {/* Export Button */}
                        <Button variant="outline" onClick={handleExport} data-testid="export-btn">
                            <Download className="h-4 w-4 mr-2" />
                            Export CSV
                        </Button>
                        
                        {/* Import Button */}
                        <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="import-btn">
                            <Upload className="h-4 w-4 mr-2" />
                            Import CSV
                        </Button>
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept=".csv"
                            onChange={handleImport}
                        />
                        
                        {/* Scan for mojibake / encoding issues (Admin only).
                            Read-only scan — never auto-corrects. */}
                        {isAdmin && (
                            <Button
                                variant="outline"
                                onClick={handleScanMojibake}
                                disabled={scanningMojibake}
                                data-testid="scan-mojibake-btn"
                                title="Scan English & translations for UTF-8 encoding corruption"
                            >
                                {scanningMojibake
                                    ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    : <AlertCircle className="h-4 w-4 mr-2" />}
                                Check encoding
                            </Button>
                        )}

                        {/* Add Key (Admin only) */}
                        {isAdmin && (
                            <Button onClick={() => setShowAddKeyDialog(true)} data-testid="add-key-btn">
                                <Plus className="h-4 w-4 mr-2" />
                                Add Key
                            </Button>
                        )}
                    </div>
                </div>

                {/* Two tabs: existing Static Strings + new Dynamic Content */}
                <Tabs defaultValue="static" className="w-full">
                    <TabsList className="mb-6" data-testid="translation-center-tabs">
                        <TabsTrigger value="static" data-testid="tab-static">Static Strings</TabsTrigger>
                        <TabsTrigger value="dynamic" data-testid="tab-dynamic">Dynamic Content</TabsTrigger>
                    </TabsList>

                    <TabsContent value="static">
                {/* Stats Cards */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <Card className="border-border">
                        <CardContent className="pt-4">
                            <div className="text-2xl font-bold text-slate-900">{stats.total_keys}</div>
                            <div className="text-sm text-slate-500">Total Keys</div>
                        </CardContent>
                    </Card>
                    <Card className="border-emerald-200 bg-emerald-50">
                        <CardContent className="pt-4">
                            <div className="text-2xl font-bold text-emerald-700 flex items-center gap-2">
                                <Check className="h-5 w-5" />
                                {stats.translated}
                            </div>
                            <div className="text-sm text-emerald-600">Translated</div>
                        </CardContent>
                    </Card>
                    <Card className="border-amber-200 bg-amber-50">
                        <CardContent className="pt-4">
                            <div className="text-2xl font-bold text-amber-700 flex items-center gap-2">
                                <AlertCircle className="h-5 w-5" />
                                {stats.missing}
                            </div>
                            <div className="text-sm text-amber-600">Missing</div>
                        </CardContent>
                    </Card>
                </div>

                {/* Search and Filters */}
                <div className="flex items-center gap-4 mb-4">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Search keys, translations, or descriptions..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                            data-testid="search-input"
                        />
                    </div>
                    <div className="flex items-center gap-2 bg-white border border-border rounded-md px-3 py-2">
                        <Filter className="h-4 w-4 text-slate-400" />
                        <Label htmlFor="show-missing" className="text-sm text-slate-600 cursor-pointer">
                            Only missing
                        </Label>
                        <Switch
                            id="show-missing"
                            checked={showOnlyMissing}
                            onCheckedChange={setShowOnlyMissing}
                            data-testid="show-missing-toggle"
                        />
                    </div>
                    {autoCapturedCount > 0 && (
                        <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-md px-3 py-2">
                            <Sparkles className="h-4 w-4 text-purple-500" />
                            <Label htmlFor="show-auto-captured" className="text-sm text-purple-700 cursor-pointer">
                                Auto-captured ({autoCapturedCount})
                            </Label>
                            <Switch
                                id="show-auto-captured"
                                checked={showAutoCaptured}
                                onCheckedChange={setShowAutoCaptured}
                                data-testid="show-auto-captured-toggle"
                            />
                        </div>
                    )}
                </div>

                {/* Translation Table */}
                <Card className="border-slate-300">
                    <CardContent className="p-0">
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                            </div>
                        ) : filteredKeys.length === 0 ? (
                            <div className="text-center py-12 text-slate-500">
                                <FileText className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                                <p>{showOnlyMissing ? 'No missing translations' : 'No translation keys found'}</p>
                                {isAdmin && !showOnlyMissing && (
                                    <Button 
                                        variant="outline" 
                                        className="mt-3"
                                        onClick={() => setShowAddKeyDialog(true)}
                                    >
                                        Add your first key
                                    </Button>
                                )}
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-border bg-background">
                                            <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider w-[22%]">
                                                Key
                                            </th>
                                            <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider w-[30%]">
                                                <div className="flex items-center gap-1.5">
                                                    {isAdmin ? (
                                                        <>
                                                            <FileText className="h-3 w-3 text-slate-400" />
                                                            English (Source · click to edit)
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Lock className="h-3 w-3 text-slate-400" />
                                                            English (Source, locked)
                                                        </>
                                                    )}
                                                </div>
                                            </th>
                                            <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider w-[38%]">
                                                {LANGUAGE_NAMES[selectedLang] || selectedLang.toUpperCase()}
                                            </th>
                                            {isAdmin && (
                                                <th className="w-12"></th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredKeys.map((key, idx) => (
                                            <tr 
                                                key={key.id}
                                                className={`border-b border-slate-100 hover:bg-background ${idx % 2 === 0 ? 'bg-white' : 'bg-background/50'} ${key.auto_captured ? 'bg-purple-50/30' : ''}`}
                                                data-testid={`translation-row-${key.key}`}
                                            >
                                                <td className="py-3 px-4">
                                                    <div className="flex items-center gap-2">
                                                        {key.auto_captured && (
                                                            <span className="flex-shrink-0" title="Auto-captured from runtime">
                                                                <Sparkles className="h-3.5 w-3.5 text-purple-500" />
                                                            </span>
                                                        )}
                                                        <code className="text-xs bg-slate-100 px-2 py-1 rounded text-slate-700">
                                                            {key.key}
                                                        </code>
                                                    </div>
                                                    {key.description && (
                                                        <p className="text-xs text-slate-400 mt-1 ml-5">{key.description}</p>
                                                    )}
                                                    {key.auto_captured && key.seen_count > 1 && (
                                                        <p className="text-xs text-purple-400 mt-0.5 ml-5">Seen {key.seen_count}x</p>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4">
                                                    {isAdmin && editingEnCell === key.id ? (
                                                        <div className="flex items-center gap-2">
                                                            <Input
                                                                value={editEnValue}
                                                                onChange={(e) => setEditEnValue(e.target.value)}
                                                                onKeyDown={(e) => handleEnKeyPress(e, key.id)}
                                                                className="text-sm"
                                                                autoFocus
                                                                data-testid={`edit-en-input-${key.key}`}
                                                            />
                                                            <Button
                                                                size="sm"
                                                                onClick={() => handleEditEnSave(key.id)}
                                                                disabled={savingEn}
                                                                data-testid={`edit-en-save-${key.key}`}
                                                            >
                                                                {savingEn ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className={`text-sm p-2 rounded border ${isAdmin ? 'cursor-pointer bg-slate-100/50 border-border hover:border-slate-400' : 'text-slate-600 bg-slate-100/50 border-border'}`}
                                                            onClick={isAdmin ? () => handleEditEnStart(key.id, key.en) : undefined}
                                                            data-testid={`en-cell-${key.key}`}
                                                            title={isAdmin ? 'Click to edit the English source. Existing translations are not affected.' : undefined}
                                                        >
                                                            {key.en}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4">
                                                    {editingCell === key.id ? (
                                                        <div className="flex items-center gap-2">
                                                            <Input
                                                                value={editValue}
                                                                onChange={(e) => setEditValue(e.target.value)}
                                                                onKeyDown={(e) => handleKeyPress(e, key.id)}
                                                                className="text-sm"
                                                                autoFocus
                                                                data-testid={`edit-input-${key.key}`}
                                                            />
                                                            <Button 
                                                                size="sm" 
                                                                onClick={() => handleEditSave(key.id)}
                                                                disabled={saving}
                                                            >
                                                                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <div 
                                                            className={`text-sm cursor-pointer p-2 rounded border border-transparent hover:border-border min-h-[36px] ${
                                                                key.is_missing ? 'bg-amber-50' : ''
                                                            }`}
                                                            onClick={() => handleEditStart(key.id, key.translation)}
                                                            data-testid={`translation-cell-${key.key}`}
                                                        >
                                                            {key.translation || (
                                                                <span className="inline-flex items-center gap-1 text-amber-600 text-xs">
                                                                    <AlertCircle className="h-3 w-3" />
                                                                    Missing
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                                {isAdmin && (
                                                    <td className="py-3 px-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                                                            onClick={() => confirmDeleteKey(key)}
                                                            data-testid={`delete-key-${key.key}`}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardContent>
                </Card>
                    </TabsContent>

                    <TabsContent value="dynamic">
                        <DynamicContentTab
                            selectedLang={selectedLang}
                            languageLabel={LANGUAGE_NAMES[selectedLang] || selectedLang.toUpperCase()}
                            isAdmin={!!isAdmin}
                        />
                    </TabsContent>
                </Tabs>
            </div>


            {/* Mojibake scan result dialog — read-only report. The
                admin fixes each entry via the inline Edit action on
                the English column; this dialog is purely diagnostic. */}
            <Dialog open={showMojibakeDialog} onOpenChange={setShowMojibakeDialog}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Encoding scan result</DialogTitle>
                        <DialogDescription>
                            Looks for UTF-8-stored-as-latin-1 corruption (strings that render as <code>â€¦</code>, <code>â€”</code>, <code>Â·</code> etc.). Nothing is auto-corrected — use the Edit action on the English column to fix each entry. Existing translations stay intact.
                        </DialogDescription>
                    </DialogHeader>
                    {mojibakeScan && (
                        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto">
                            <div className="grid grid-cols-3 gap-3 text-center">
                                <div className="p-3 rounded border bg-slate-50">
                                    <div className="text-2xl font-bold text-slate-700" data-testid="scan-total">{mojibakeScan.total_affected}</div>
                                    <div className="text-xs text-slate-500 uppercase tracking-wide">Total affected</div>
                                </div>
                                <div className="p-3 rounded border bg-amber-50">
                                    <div className="text-2xl font-bold text-amber-700" data-testid="scan-keys">{mojibakeScan.translation_keys.affected}</div>
                                    <div className="text-xs text-amber-700 uppercase tracking-wide">English source rows</div>
                                </div>
                                <div className="p-3 rounded border bg-amber-50">
                                    <div className="text-2xl font-bold text-amber-700" data-testid="scan-values">{mojibakeScan.translation_values.affected}</div>
                                    <div className="text-xs text-amber-700 uppercase tracking-wide">Translation rows</div>
                                </div>
                            </div>
                            {mojibakeScan.translation_keys.sample.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-semibold mb-2 text-slate-700">Sample · English source rows (first 10)</h4>
                                    <div className="space-y-2">
                                        {mojibakeScan.translation_keys.sample.map((s) => (
                                            <div key={s.id} className="text-xs p-2 rounded border bg-amber-50/40">
                                                <code className="text-slate-700">{s.key}</code>
                                                <div className="mt-1 text-slate-500">Current: <span className="font-mono text-rose-700 break-all">{s.current}</span></div>
                                                <div className="text-slate-500">Would become: <span className="font-mono text-emerald-700 break-all">{s.would_become}</span></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {mojibakeScan.translation_values.sample.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-semibold mb-2 text-slate-700">Sample · Translation rows (first 10)</h4>
                                    <div className="space-y-2">
                                        {mojibakeScan.translation_values.sample.map((s, i) => (
                                            <div key={i} className="text-xs p-2 rounded border bg-amber-50/40">
                                                <code className="text-slate-700">{s.lang}</code>
                                                <div className="mt-1 text-slate-500">Current: <span className="font-mono text-rose-700 break-all">{s.current}</span></div>
                                                <div className="text-slate-500">Would become: <span className="font-mono text-emerald-700 break-all">{s.would_become}</span></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            <p className="text-xs text-slate-500">{mojibakeScan.note}</p>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowMojibakeDialog(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Key Dialog */}
            <Dialog open={showAddKeyDialog} onOpenChange={setShowAddKeyDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add Translation Key</DialogTitle>
                        <DialogDescription>
                            Create a new translation key with English as the source text.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label htmlFor="key">Key</Label>
                            <Input
                                id="key"
                                placeholder="e.g., order.create.title"
                                value={newKey.key}
                                onChange={(e) => setNewKey(prev => ({ ...prev, key: e.target.value.toLowerCase() }))}
                                data-testid="new-key-input"
                            />
                            <p className="text-xs text-slate-500 mt-1">
                                Lowercase words with dots (e.g., common.button.save)
                            </p>
                        </div>
                        <div>
                            <Label htmlFor="en" className="flex items-center gap-1.5">
                                <Lock className="h-3 w-3 text-slate-400" />
                                English Text (Source)
                            </Label>
                            <Textarea
                                id="en"
                                placeholder="Enter the English text..."
                                value={newKey.en}
                                onChange={(e) => setNewKey(prev => ({ ...prev, en: e.target.value }))}
                                data-testid="new-en-input"
                            />
                            <p className="text-xs text-slate-500 mt-1">
                                English is the source of truth and cannot be empty
                            </p>
                        </div>
                        <div>
                            <Label htmlFor="description">Description (optional)</Label>
                            <Input
                                id="description"
                                placeholder="Context or usage notes for translators..."
                                value={newKey.description}
                                onChange={(e) => setNewKey(prev => ({ ...prev, description: e.target.value }))}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowAddKeyDialog(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleAddKey} data-testid="create-key-btn">
                            Create Key
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="text-red-600">Delete Translation Key</DialogTitle>
                        <DialogDescription>
                            This action cannot be undone. This will permanently delete the key and all its translations.
                        </DialogDescription>
                    </DialogHeader>
                    {keyToDelete && (
                        <div className="py-4">
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                <p className="text-sm font-medium text-red-800">
                                    Key: <code className="bg-red-100 px-1 rounded">{keyToDelete.key}</code>
                                </p>
                                <p className="text-sm text-red-700 mt-1">
                                    English: "{keyToDelete.en}"
                                </p>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteKey} data-testid="confirm-delete-btn">
                            Delete Key
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Import Result Dialog */}
            <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Import Complete</DialogTitle>
                    </DialogHeader>
                    {importResult && (
                        <div className="space-y-4 py-4">
                            <div className="grid grid-cols-3 gap-4">
                                <div className="bg-emerald-50 p-3 rounded-lg text-center">
                                    <div className="text-lg font-bold text-emerald-700">{importResult.imported}</div>
                                    <div className="text-xs text-emerald-600">New</div>
                                </div>
                                <div className="bg-blue-50 p-3 rounded-lg text-center">
                                    <div className="text-lg font-bold text-blue-700">{importResult.updated}</div>
                                    <div className="text-xs text-blue-600">Updated</div>
                                </div>
                                <div className="bg-slate-100 p-3 rounded-lg text-center">
                                    <div className="text-lg font-bold text-slate-700">{importResult.skipped}</div>
                                    <div className="text-xs text-slate-600">Skipped</div>
                                </div>
                            </div>
                            
                            {importResult.unknown_keys?.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-medium text-amber-700 mb-2">Unknown Keys (not imported):</h4>
                                    <div className="max-h-24 overflow-y-auto bg-amber-50 p-2 rounded text-xs">
                                        {importResult.unknown_keys.map((k, i) => (
                                            <div key={i} className="text-amber-800">{k}</div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {importResult.skipped_reasons?.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-medium text-slate-600 mb-2">Skip Reasons:</h4>
                                    <div className="max-h-24 overflow-y-auto bg-background p-2 rounded text-xs">
                                        {importResult.skipped_reasons.map((r, i) => (
                                            <div key={i} className="text-slate-600">{r}</div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            
                            {importResult.errors?.length > 0 && (
                                <div>
                                    <h4 className="text-sm font-medium text-red-700 mb-2">Errors:</h4>
                                    <div className="max-h-24 overflow-y-auto bg-red-50 p-2 rounded text-xs">
                                        {importResult.errors.map((e, i) => (
                                            <div key={i} className="text-red-800">{e}</div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button onClick={() => setShowImportDialog(false)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
